import { ActionPermission, PermissionsUtil, StringUtil } from "@mcleod/core";
import {
    AddType, Attachment, Button, Component, DataDisplayEvent, DataSourceMode, Label, LookupModelSearchEvent,
    SlideoutDecorator, TableRowBeforeSaveEvent, TableRowDisplayEvent, TableRowMode, Textbox
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { AutogenLayoutOrderPostHist } from "./autogen/AutogenLayoutOrderPostHist";

const REQUIRE_REASON_TYPES = [
    "B", // unassign tractor / driver type
    "Z", // feasibility type
    "V", // void order type
];

export interface OrderPostHistProps {
    orderType: string;
    orderId: string;
    movementId: string;
    isBilled: boolean;
    allowDelete?: boolean;
}

export class OrderPostHist extends AutogenLayoutOrderPostHist {
    private props: OrderPostHistProps = {
        orderType: "",
        orderId: "",
        movementId: "",
        isBilled: false
    }

    public static showInSlideout(props: OrderPostHistProps, mode: DataSourceMode, button: Button) {
        if (button) button.busy = true;

        OrderPostHist.getLayout("lme/dispatch/OrderPostHist", { width: window.innerWidth * .8 }).addLayoutLoadListener(event => {
            const layout = event.target as OrderPostHist;
            layout.props = props;
            layout.initialize().then(() => {
                layout.width = window.innerWidth * .7;
                SlideoutDecorator.decorateAndSlideIn(layout, {
                    title: layout.getSlideoutTitle(props.orderType) + " - Order " + props.orderId,
                    titleImage: undefined,
                    fillVerticalSpace: true,
                    doAfterSlideIn: (decorator: SlideoutDecorator) => {
                        if (button) button.busy = false;
                        layout.tableOrderPostHist.dataSource.mode = mode;
                    }
                });
            }).catch(error => {
                CommonDialogs.showError(error);
                if (button) button.busy = false;
            })
        })
    }

    private getSlideoutTitle(orderType: string) {
        return orderType === "QUOTE" ? "Quote History" : "Order History";
    }

    public async initialize(): Promise<any> {
        const isQuote = this.props.orderType === "QUOTE";
        if (this.props.orderId != null && (this.props.movementId != null || isQuote)) {
            this.tableOrderPostHist.persistChangesImmediately = true;
            if (isQuote) {
                this.tableOrderPostHist.dataSource = this.sourceQuotePostHist;
                this.tableOrderPostHist.allowDelete = this.props.allowDelete ?? true;
                this.tableOrderPostHist.removeColumn("Movement");
            }
            await this.tableOrderPostHist.dataSource.search({ order_id: this.props.orderId });
            if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_ADD_ORDERHIST_AFTER_BILL) && this.props.isBilled) {
                this.tableOrderPostHist.addType = AddType.NOT_ALLOWED;
            }
        } else {
            return Promise.reject("orders.id and movement.id is required");
        }
    }

    /** This is an event handler for the onRowDisplay event of tableOrderPostHist.  */
    tableOrderPostHistOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        if (tableRow.mode !== TableRowMode.QUICK_ADD && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_ORDERHIST_AFTER_BILL) && this.props.isBilled) {
            tableRow.allowEdit = false;
        }

        const textboxPostedType = tableRow.findComponentById("textboxPostedType") as Textbox;
        textboxPostedType.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.allow_manual_entry = "Y"
        });
        textboxPostedType.enabled = modelRow._appending;

        const textboxReasonCode = tableRow.findComponentById("textboxReasonCode") as Textbox;
        textboxReasonCode.enabled = !modelRow._appending && REQUIRE_REASON_TYPES.indexOf(modelRow.get("posted_type")) > -1;
        if (textboxReasonCode.enabled)
            textboxReasonCode.required = true;
        textboxReasonCode.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.code_type = modelRow.get("posted_type");
        });

        const attachment = tableRow.findComponentById("attachmentFile") as Attachment;
        attachment.tooltip = () => attachment.fileName;

        if (tableRow.mode === TableRowMode.QUICK_ADD && this.props.movementId)
            (tableRow.findComponentById("labelMovement") as Label).text = this.props.movementId;

        const textboxBackOffCode = tableRow.findComponentById("textboxArReasonCode") as Textbox;
        textboxBackOffCode.visible = !modelRow.isNull("ar_reason_code");
        textboxReasonCode.visible = modelRow.isNull("ar_reason_code");
    }

    tableOrderPostHistBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const data = event.getTableRow().data;
        if (data._appending) {
            data.set("movement_id", this.props.movementId);
            data.set("order_id", this.props.orderId);
        }
    }

    labelPostedByOnDataDisplay(event: DataDisplayEvent) {
        const labelPostedBy = event.target as Label;
        CommonTooltips.setTooltipContentLayout(labelPostedBy, event.rowData?.get("posted_by"), "common/UserQuickInfo");
    }

    internalIsEmpty(component: Component) {
        if (component == null)
            return true;
        if (component instanceof Textbox || component instanceof Label)
            return StringUtil.isEmptyString(component.text);
        return false;
    }
}
